// GOV-7 Phase 2 — PUBLIC customer tracking map (Google Maps JavaScript = VISUAL RENDERER ONLY).
//
// Renders ONLY the backend-projected customer-safe geometry: current mover position, the decoded
// route.encoded_polyline, and pickup / destination / active-destination markers. It performs NO routing:
// the browser never calls Directions / Routes / DistanceMatrix and never computes an ETA — that stays the
// server-side LocationEtaEngine (the ONE route/ETA authority). Markers use GENERIC customer-safe labels
// only ("Your movers" / "Pickup" / "Destination"): never an operator name, staff/vehicle/tracker id,
// location-source enum, provider identifier, or any commercial/profile metadata.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { loadGoogleMaps, googleMapsKey } from '../../lib/googleMapsLoader';
import { decodePolyline } from '../../lib/polyline';

const LABEL_MOVER = 'Your movers';
const LABEL_PICKUP = 'Pickup';
const LABEL_DESTINATION = 'Destination';

function ll(p) {
  return p && typeof p.lat === 'number' && typeof p.lng === 'number' ? { lat: p.lat, lng: p.lng } : null;
}

// PURE — derive presentation geometry from the BACKEND projection. No routing / no ETA math here.
export function deriveMapModel(data) {
  const d = data || {};
  const ad = d.active_destination || null;
  const activeType = ad && (ad.type === 'pickup' || ad.type === 'destination') ? ad.type : null;
  const route = d.route && typeof d.route.encoded_polyline === 'string' ? decodePolyline(d.route.encoded_polyline) : [];
  return {
    mover: ll(d.current_position),
    pickup: ll(d.pickup),
    destination: ll(d.destination),
    activeType,
    route,
    ended: d.tracking_ended === true,
  };
}

const TrackingMap = ({ data }) => {
  const key = googleMapsKey();
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const overlaysRef = useRef([]);
  const [status, setStatus] = useState('init'); // init | ready | failed

  const model = useMemo(() => deriveMapModel(data), [data]);
  const modelKey = useMemo(() => JSON.stringify(model), [model]);
  const hasGeometry = !!(model.mover || model.pickup || model.destination || model.route.length);

  // Load the Maps SDK once (key gated). Absent key → clean text-only page; load failure → truthful notice.
  useEffect(() => {
    if (!key) return;
    let alive = true;
    loadGoogleMaps()
      .then(() => { if (alive) setStatus('ready'); })
      .catch(() => { if (alive) setStatus('failed'); });
    return () => { alive = false; };
  }, [key]);

  // Draw / redraw when the SDK is ready and geometry changes (poll updates flow through modelKey).
  useEffect(() => {
    if (status !== 'ready' || !containerRef.current || !(window.google && window.google.maps)) return;
    const maps = window.google.maps;
    if (!mapRef.current) {
      mapRef.current = new maps.Map(containerRef.current, {
        center: { lat: 0, lng: 0 }, zoom: 12, clickableIcons: false,
        mapTypeControl: false, streetViewControl: false, fullscreenControl: false,
      });
    }
    // clear previous overlays (single map instance; no duplicate layers on refresh)
    overlaysRef.current.forEach((o) => { try { o.setMap(null); } catch (_) { /* noop */ } });
    overlaysRef.current = [];

    const bounds = new maps.LatLngBounds();
    let any = false;
    const addMarker = (pos, title) => {
      if (!pos) return;
      const m = new maps.Marker({ position: pos, map: mapRef.current, title });
      overlaysRef.current.push(m);
      bounds.extend(pos); any = true;
    };

    // Backend-projected route geometry ONLY (decoded server polyline).
    if (model.route.length > 1) {
      const pl = new maps.Polyline({
        path: model.route, map: mapRef.current, geodesic: true,
        strokeColor: '#7c3aed', strokeOpacity: 0.9, strokeWeight: 5,
      });
      overlaysRef.current.push(pl);
      model.route.forEach((p) => { bounds.extend(p); any = true; });
    }

    if (!model.ended) addMarker(model.mover, LABEL_MOVER);
    addMarker(model.pickup, LABEL_PICKUP);
    addMarker(model.destination, LABEL_DESTINATION);

    // §11 viewport: fit to the relevant customer-safe geometry (mover + active route + targets).
    if (any) { try { mapRef.current.fitBounds(bounds); } catch (_) { /* noop */ } }
  }, [status, modelKey, model]);

  if (!key) return null;                 // misconfigured build → page stays truthful text-only
  if (status === 'failed') {
    return (
      <div data-testid="track-map-failed" role="status"
           className="rounded-lg bg-gray-50 p-4 text-center text-gray-600 text-sm">
        The live map couldn’t load. Your ETA and status above are still accurate.
      </div>
    );
  }
  if (!hasGeometry) return null;         // nothing to draw yet — the text ETA/status still shows

  return (
    <div className="rounded-lg overflow-hidden border border-gray-100" data-testid="track-map">
      <div
        ref={containerRef}
        data-testid="track-map-canvas"
        role="application"
        aria-label="Live tracking map"
        style={{ width: '100%', height: '320px' }}
      />
      {status !== 'ready' && (
        <div className="py-6 text-center text-gray-400 text-sm" data-testid="track-map-loading">Loading map…</div>
      )}
    </div>
  );
};

export default TrackingMap;
